import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { TemplateFieldEditor } from '@/components/templates-v2/document-generation/templates/TemplateInstantiator';
import { Button } from '@/components/ui/catalyst/button';
import { usePackageComponents } from '@/hooks/usePackageComponents';
import { cn } from '@/lib/utils';
import type {
  PackageComponentSchema,
  PackageRequestInfo,
  StaticDocumentTag,
} from '@/services/api/hipaaPackageService';
import { MatterFlattenedContext } from '@/services/api/matterFlattenedContextService';

import { AnnotationFieldsEditor } from './MissingFieldsEditor';
import { useAnnotationFields } from './useAnnotationFields';

interface ComponentItemProps {
  component: PackageComponentSchema;
  packageId: string;
  requestInfo: PackageRequestInfo;
  matterContext?: MatterFlattenedContext | undefined;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onUpdate: () => void;
  disabled?: boolean;
  packageMode: 'view' | 'edit';
  onOpenDocumentEditor?: (componentId?: string) => void;
}

const StaticDocumentLabels: Record<StaticDocumentTag, string> = {
  client_id: "Client's Photo ID",
  attorney_relationship: 'Attorney Relationship Form',
  reproductive_rights: 'Reproductive Rights Attestation',
  texas_affidavit: 'Texas Affidavit',
};

/**
 * Component item with expandable configuration
 * Displays component status and allows inline editing
 */
export const ComponentItem: React.FC<ComponentItemProps> = ({
  component,
  packageId,
  requestInfo,
  matterContext = {},
  isSelected,
  onSelect,
  onRemove,
  onUpdate,
  disabled = false,
  packageMode,
  onOpenDocumentEditor,
}) => {
  const [showFields, setShowFields] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, string>>(
    component.overrides || {},
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleRefetch = async () => {
    await onUpdate();
  };

  const { updateComponentOverrides } = usePackageComponents({
    packageId,
    requestInfo,
    components: [component],
    onRefetch: handleRefetch,
  });

  // Fetch annotation fields for signed_authorization_merge components
  const isSignedAuthMerge =
    component.component_type === 'signed_authorization_merge';
  const { annotationFields, isLoading: isLoadingAnnotations } =
    useAnnotationFields({
      componentId: component.id,
      packageId,
      requestInfo,
      skip: !isSignedAuthMerge,
      isExpanded: showFields && isSignedAuthMerge,
    });

  // Sync local overrides when component data changes
  useEffect(() => {
    setOverrides(component.overrides || {});
  }, [component.overrides]);

  const handleSetOverride = (fieldId: string, value: string) => {
    setOverrides((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleSaveOverrides = async () => {
    setIsSaving(true);
    try {
      await updateComponentOverrides(component.id, overrides);
      toast.success('Fields saved - package re-rendering', {
        description: 'Your changes are being processed',
      });
      setShowFields(false);
    } catch (error) {
      toast.error('Failed to update fields');
    } finally {
      setIsSaving(false);
    }
  };

  // Compute effective status: treat missing_fields with 0 actual missing fields as ready
  const effectiveStatus =
    component.status === 'missing_fields' &&
    Object.keys(component.missing_fields || {}).length === 0
      ? 'ready'
      : component.status;

  const getStatusIndicator = () => {
    switch (effectiveStatus) {
      case 'ready':
        return <CheckCircleIcon className="h-3.5 w-3.5 text-green-500" />;
      case 'missing_fields':
        return (
          <ExclamationTriangleIcon className="h-3.5 w-3.5 text-amber-500" />
        );
      case 'error':
        return <ExclamationCircleIcon className="h-3.5 w-3.5 text-red-500" />;
      case 'generating':
        return (
          <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
        );
      case 'published':
        return <CheckCircleIcon className="h-3.5 w-3.5 text-blue-500" />;
      default:
        return null;
    }
  };

  const isSignedAuthorizationMerge =
    component.component_type === 'signed_authorization_merge';

  const signedAuthorizationDocumentName =
    component.blob?.original_filename || component.blob?.name || undefined;

  const signedAuthorizationTemplateName =
    component.template?.display_name || component.template?.name || undefined;

  const getComponentTitle = () => {
    if (component.component_type === 'template') {
      return (
        component.template?.display_name ||
        component.template?.name ||
        component.template?.category ||
        'Untitled Template'
      );
    }

    if (isSignedAuthorizationMerge) {
      return (
        signedAuthorizationDocumentName ||
        signedAuthorizationTemplateName ||
        'Signed Authorization Merge'
      );
    }

    if (
      component.component_type === 'static_document' &&
      component.static_document_tag
    ) {
      return StaticDocumentLabels[component.static_document_tag];
    }

    if (component.component_type === 'matter_blob') {
      return (
        component?.blob?.name ||
        component?.blob?.original_filename ||
        'Untitled Document'
      );
    }

    return 'Untitled Document';
  };

  const getMissingFieldsCount = () => {
    return Object.keys(component.missing_fields || {}).length;
  };

  const hasMissingFields = getMissingFieldsCount() > 0;
  const hasChanges =
    JSON.stringify(overrides) !== JSON.stringify(component.overrides || {});
  const canExpand =
    (component.component_type === 'template' ||
      component.component_type === 'signed_authorization_merge') &&
    component.v2_template_id;

  const isEditMode = packageMode === 'edit';
  const isInteractionDisabled = disabled || !isEditMode;
  const canEditInDocumentEditor =
    component.component_type !== 'signed_authorization_merge' &&
    Boolean(component.rendered_docx_url);
  const editLinkLabel = (() => {
    if (onOpenDocumentEditor && canEditInDocumentEditor) {
      return 'Edit Document';
    }

    return showFields ? 'Close' : 'Edit';
  })();

  // Sortable hook
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: component.id,
    disabled: isInteractionDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isGenerating = component.status === 'generating';
  const signedAuthorizationFields = isLoadingAnnotations ? (
    <p className="text-xs text-gray-500">Loading fields...</p>
  ) : (
    <AnnotationFieldsEditor
      annotationFields={annotationFields}
      overrides={overrides}
      setOverride={handleSetOverride}
    />
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-[18px] border transition-all',
        isSelected && 'border-indigo-300 bg-white shadow-sm ring-2 ring-indigo-100',
        !isSelected &&
          isGenerating &&
          'border-blue-200 bg-blue-50/30 animate-pulse',
        !isSelected &&
          !isGenerating &&
          'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm',
        isDragging && 'opacity-50 shadow-lg',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      {/* Main Component Row */}
      <div
        className={cn(
          'group relative cursor-pointer',
          showFields && 'border-b border-gray-200',
        )}
        onClick={() => {
          if (!isInteractionDisabled) {
            onSelect();
          }
        }}
        onKeyDown={(e) => {
          if (!isInteractionDisabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onSelect();
          }
        }}
        role="button"
        tabIndex={isInteractionDisabled ? -1 : 0}
      >
        <div className="flex gap-3 px-4 py-3.5">
          {/* Drag Handle - only show in edit mode */}
          {isEditMode && (
            <button
              type="button"
              {...attributes}
              {...listeners}
              className={cn(
                'flex-shrink-0 self-start cursor-grab p-0.5 text-zinc-400 transition-colors hover:text-zinc-600 active:cursor-grabbing',
                disabled && 'cursor-not-allowed opacity-50',
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <Bars3Icon className="h-4 w-4" />
            </button>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-1.5">
            {/* Title */}
            <div className="text-[15px] font-medium leading-snug text-zinc-900">
              {getComponentTitle()}
            </div>

            {/* Meta Info */}
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              {/* Type */}
              <span>
                {component.component_type === 'template' && 'Template'}
                {component.component_type === 'matter_blob' && 'Document'}
                {component.component_type === 'static_document' &&
                  'Firm Document'}
                {isSignedAuthorizationMerge && 'Signed Authorization'}
              </span>

              {/* Status */}
              {getStatusIndicator() && (
                <>
                  <span>•</span>
                  <div className="flex items-center gap-1">
                    {getStatusIndicator()}
                    <span>
                      {effectiveStatus === 'ready' && 'Ready'}
                      {effectiveStatus === 'missing_fields' &&
                        `${getMissingFieldsCount()} missing`}
                      {effectiveStatus === 'error' && 'Error'}
                      {effectiveStatus === 'generating' && 'Generating'}
                      {effectiveStatus === 'published' && 'Published'}
                    </span>
                  </div>
                </>
              )}

              {/* Edit Link - only show in edit mode */}
              {canExpand && isEditMode && (
                <>
                  <span>•</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onOpenDocumentEditor && canEditInDocumentEditor) {
                        onOpenDocumentEditor(component.id);
                        return;
                      }

                      setShowFields(!showFields);
                    }}
                    className={cn(
                      'hover:underline',
                      hasMissingFields ? 'text-amber-600' : 'text-gray-600',
                      !hasMissingFields && 'text-orange-500',
                    )}
                  >
                    {editLinkLabel}
                  </button>
                </>
              )}
            </div>

            {isSignedAuthorizationMerge &&
              (signedAuthorizationDocumentName ||
                signedAuthorizationTemplateName) && (
                <div className="text-[11px] text-gray-500 leading-tight">
                  {signedAuthorizationDocumentName && (
                    <div>Signed PDF: {signedAuthorizationDocumentName}</div>
                  )}
                  {signedAuthorizationTemplateName && (
                    <div>Template: {signedAuthorizationTemplateName}</div>
                  )}
                </div>
              )}
          </div>

          {/* Remove Button - only show in edit mode */}
          {isEditMode && (
            <button
              type="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (!disabled) {
                  onRemove();
                }
              }}
              className={cn(
                'flex-shrink-0 self-start text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all',
                'p-1 rounded hover:bg-red-50',
                disabled && 'cursor-not-allowed',
              )}
              disabled={disabled}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded Fields Section - only editable in edit mode */}
      {showFields &&
        (component.component_type === 'template' ||
          component.component_type === 'signed_authorization_merge') &&
        component.v2_template_id &&
        isEditMode && (
          <div className="p-4 bg-gray-50/50 space-y-3">
            {component.component_type === 'signed_authorization_merge' ? (
              signedAuthorizationFields
            ) : (
              <TemplateFieldEditor
                templateId={component.v2_template_id || ''}
                matterContext={
                  matterContext as Record<string, string | undefined>
                }
                overrides={overrides}
                setOverride={handleSetOverride}
                inline
              />
            )}

            {hasChanges && (
              <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                <Button
                  onClick={() => {
                    setOverrides(component.overrides || {});
                    setShowFields(false);
                  }}
                  outline
                  className="text-sm h-8 px-3"
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveOverrides}
                  className="text-sm h-8 px-3"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save & Re-render'}
                </Button>
              </div>
            )}
          </div>
        )}
    </div>
  );
};
